import React, { useState } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Checkbox, DatePicker, Dot, Icon } from '@fikasio/react-ui-components';
import TrashIcon from 'components/UI/TrashIcon';
import ProjectTag from 'components/projects/ProjectTag';
import { projectStatus } from 'components/projects/utils';
import OBJECTIVE_STATUS_OPTIONS from 'components/objectives/objectiveStatusOptions';
import AssigneeButton from 'components/tasks/TasksViewer/AssigneeButton';
import ProjectSelectorButton from 'components/tasks/TasksViewer/ProjectSelectorButton';
import TaskStatusDropdown from 'components/tasks/TasksViewer/TaskStatusDropdown';
import { statusChangePatch, visibleStatus } from 'components/tasks/TasksViewer/statusChange';
import DropdownToggle from 'components/UI/DropdownToggle';
import { useAddObjectiveMutation, usePatchObjectiveMutation, useDeleteObjectiveMutation } from 'services/objectives/api';
import { useGetProjectsQuery } from 'services/projects/api';
import { Objective } from 'services/objectives/types';
import { RangeSelectEvent } from 'utils/rangeSelection';
import { formatPickerDate, pickedDateTimeInUserZone, toPickerDate } from 'utils/date';
import { formatObjectiveProgress, getObjectiveStatusColor, isObjectiveBehind, objectiveStatus } from './utils';
import styles from './ObjectivesList.module.css';

export interface ObjectiveRowProps {
  isSelected?: boolean;
  onAddObjective: (objective: Objective) => Promise<void>;
  onClick: (objectiveId: string) => Promise<void>;
  onSelect: (objective: Objective, event?: RangeSelectEvent) => void;
  objective: any;
  projectId?: string;
}

const ObjectiveRow = ({
  isSelected,
  objective,
  onAddObjective: _onAddObjective,
  onClick,
  onSelect,
  projectId: _projectId,
}) => {
  const { t } = useTranslation();

  const [isDueAtDatepickerOpen, setIsDueAtDatepickerOpen] = useState(false);
  const [addedProjectIds, setAddedProjectIds] = useState<string[]>([]);

  const { data: projects } = useGetProjectsQuery();
  const [createObjective] = useAddObjectiveMutation();
  const [patchObjective] = usePatchObjectiveMutation();
  const [deleteObjective] = useDeleteObjectiveMutation();

  const savedProjects = objective.projects || [];
  const assignedProjects = [
    ...savedProjects,
    ...addedProjectIds
      .filter(id => !savedProjects.some(project => project.id === id))
      .map(id => ({ id })),
  ];

  const isLate = isObjectiveBehind(objective);
  const color = getObjectiveStatusColor(objective);
  const assignedProjectIds = new Set(assignedProjects.map(p => p.id));
  const availableProjects = (projects || [])
    .filter(p => !p.isArchived && projectStatus(p) !== 'Cancelled' && !assignedProjectIds.has(p.id))
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

  const handleProjectSelect = (project: { id: string }) => {
    const updatedProjects = [...assignedProjects, { id: project.id }];
    setAddedProjectIds(current => (
      current.includes(project.id) ? current : [...current, project.id]
    ));
    if (objective.id) {
      patchObjective({
        id: objective.id,
        projects: updatedProjects,
      });
    }
  };

  return (
    <tr
      className={classNames(styles.objectiveRow, {
        selected: isSelected,
      })}
    >
      <td
        onClick={event => {
          event.stopPropagation();
          onSelect(objective, event);
        }}
        onMouseDown={event => {
          if (event.shiftKey) {
            event.preventDefault();
          }
        }}
        style={{
          textAlign: 'center',
        }}
        width={35}
      >
        <Checkbox isChecked={isSelected} />
      </td>
      <td
        className={styles.objectiveRowStatus}
        width={35}
      >
        <TaskStatusDropdown
          onChange={(nextStatus, extras) => {
            patchObjective({
              id: objective.id,
              ...statusChangePatch(nextStatus, {
                cancelReason: extras?.cancelReason,
                wasArchived: objective.isArchived,
              }),
            });
          }}
          status={visibleStatus(objectiveStatus(objective), objective.isArchived)}
          statusOptions={OBJECTIVE_STATUS_OPTIONS}
        />
      </td>
      <td
        onClick={event => {
          if (event.shiftKey) {
            event.preventDefault();
            onSelect(objective, event);
            return;
          }
          onClick?.(objective.id);
        }}
        onMouseDown={event => {
          if (event.shiftKey) {
            event.preventDefault();
          }
        }}
        style={{
          cursor: 'pointer',
          paddingLeft: 8,
        }}
      >
        { objective.name }
      </td>
      <td
        className={styles.objectiveRowProjects}
        width={160}
      >
        {
          assignedProjects.map(p => (
            <ProjectTag
              key={p.id}
              projectId={p.id}
            />
          ))
        }
        <ProjectSelectorButton
          onSelect={handleProjectSelect}
          projects={availableProjects}
        />
      </td>
      <td width={140}>
        {formatObjectiveProgress(objective, t)}
      </td>
      <td
        className={styles.objectiveRowDueDate}
        onClick={() => setIsDueAtDatepickerOpen(true)}
        style={{
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          ...(isLate && { color: '#ff0000' }),
          width: 110,
        }}
      >
        <DatePicker
          defaultValue={toPickerDate(objective.dueDate)}
          displayFunction={formatPickerDate}
          isOpen={isDueAtDatepickerOpen}
          onChange={value => {
            const timestamp = pickedDateTimeInUserZone(value)
              .set({ hour: 23, minute: 59, second: 59, millisecond: 999 });
            patchObjective({
              id: objective.id,
              dueDate: timestamp.toISO(),
            });
          }}
          onClose={() => setIsDueAtDatepickerOpen(false)}
          onRemoveValue={e => {
            e.stopPropagation();
            patchObjective({
              id: objective.id,
              dueDate: null,
            });
          }}
          shouldCloseOnSelect
          showRemoveValue
          showTimeSelect={false}
        />
      </td>
      <td
        className={styles.objectiveRowAssignee}
        width={35}
      >
        <AssigneeButton
          assigneeId={objective.assignee || undefined}
          onChange={assignee => {
            patchObjective({
              assignee,
              id: objective.id,
            });
          }}
        />
      </td>
      <td
        style={{
          textAlign: 'center',
        }}
        width={35}
      >
        <Dropdown
          style={{
            position: 'static',
          }}
        >
          <Dropdown.Toggle as={DropdownToggle} />
          <Dropdown.Menu>
            <Dropdown.Item
              onClick={() => createObjective(objective)}
            >
              <Icon
                name="copy"
                style={{
                  marginRight: 10,
                  width: 25,
                }}
              />
              {t('duplicate')}
            </Dropdown.Item>
            <Dropdown.Item
              onClick={() => deleteObjective(objective.id)}
            >
              <TrashIcon
                style={{
                  color: 'red',
                  marginRight: 10,
                  width: 25,
                }}
              />
              {t('delete')}
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </td>
    </tr>
  );
};

export default ObjectiveRow;
